import type { MouseEvent, PropsWithChildren } from "react";

import type { Placement } from "@similarweb/ui-components";
import { LinkElement, Tooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import InteractiveTooltip from "app/components/React/Tooltip/InteractiveTooltip/InteractiveTooltip";
import { useTranslation } from "pro-features/components/WithTranslation/src/I18n";
import { MAX_WIDTH_QUOTA_TOOLTIP, MAX_WIDTH_QUOTA_TOOLTIP_TRIAL } from "app/pages/sales-intelligence/constants/common";
import { is } from "app/pages/sales-intelligence/utils/type-guards";

import { StyledTooltipBody, StyledTooltipHeader, StyledTooltipLinkWrapper } from "./style";

type WrappedWithQuotaTooltipProps = {
    isListSizeQuotaReached?: boolean;
    quotaLimit?: number;
    placement?: Placement;
    isTrialTooltip?: boolean;
};

const WrappedWithQuotaTooltip = ({
    children,
    isListSizeQuotaReached,
    quotaLimit,
    placement = "bottom",
    isTrialTooltip,
}: PropsWithChildren<WrappedWithQuotaTooltipProps>) => {
    const translate = useTranslation();

    const handleRedirectToLists = (e: MouseEvent) => {
        e.preventDefault();
        void siNavigator.goToListsHomePage();
    };

    if (isListSizeQuotaReached && isTrialTooltip) {
        return (
            <Tooltip
                enabled={isListSizeQuotaReached}
                placement={placement}
                content={
                    <Box maxWidth={`${MAX_WIDTH_QUOTA_TOOLTIP_TRIAL}px`}>
                        <StyledTooltipHeader>{translate("si.quotaLimit.tooltip.header.trial")}</StyledTooltipHeader>
                        <StyledTooltipBody>{translate(`si.quotaLimit.tooltip.body.trial`)}</StyledTooltipBody>
                    </Box>
                }
            >
                <div>{children}</div>
            </Tooltip>
        );
    }

    return (
        <InteractiveTooltip
            enabled={isListSizeQuotaReached}
            placement={placement}
            maxWidth={MAX_WIDTH_QUOTA_TOOLTIP}
            tooltipContent={
                <div>
                    <StyledTooltipHeader>{translate("si.quotaLimit.tooltip.header")}</StyledTooltipHeader>
                    <StyledTooltipBody>
                        {is.undefined(quotaLimit)
                            ? translate(`si.quotaLimit.tooltip.body`)
                            : translate(`si.quotaLimit.tooltip.quotaCountBody`, { quotaLimit })}
                    </StyledTooltipBody>
                    <StyledTooltipLinkWrapper>
                        {/* @ts-expect-error - (e: MouseEvent) => void is not assignable to () => void */}
                        <LinkElement onClick={handleRedirectToLists}>
                            {translate("si.quotaLimit.tooltip.linkText")}
                        </LinkElement>
                    </StyledTooltipLinkWrapper>
                </div>
            }
        >
            <div>{children}</div>
        </InteractiveTooltip>
    );
};

export default WrappedWithQuotaTooltip;
